import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, Sparkles, ExternalLink, Monitor } from 'lucide-react';
import { useApi } from '../api-context';
import type { BlockReason } from '../../api';

/**
 * Shown when this browser cannot open a folder.
 *
 * Culling is a long investment before any payoff, so it is kinder to say this
 * before someone starts than to strand them at export. What we say depends on
 * why: Brave is one setting away from working, Safari and Firefox are not.
 */
export default function Unsupported({ reason }: { reason: BlockReason }) {
  const api = useApi();
  const navigate = useNavigate();
  const [copied, setCopied] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const copy = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1800);
  };

  const tryDemo = async () => {
    setLoading(true);
    const result = await api.importDemo();
    navigate(`/p/${result.project.id}/review`);
  };

  return (
    <div id="main" className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-xl">
        {reason === 'brave-flag-off' ? <BraveFix onCopy={copy} copied={copied} /> : null}
        {reason === 'mobile' ? <OnAPhone onCopy={copy} copied={copied} /> : null}
        {reason === 'no-api' ? <NoApi onCopy={copy} copied={copied} /> : null}
        {reason === 'no-image-pipeline' ? <TooOld /> : null}

        <div className="mt-8 border-t border-ink-850 pt-6">
          <button className="btn-outline" onClick={tryDemo} disabled={loading}>
            <Sparkles size={15} />
            {reason === 'mobile' ? 'Try the sample shoot anyway' : 'Look around with sample photos'}
          </button>
          <p className="mt-3 text-tiny text-ink-500">
            The sample shoot works here. You can cull it, you just cannot export from it.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function BraveFix({
  onCopy,
  copied,
}: {
  onCopy: (text: string, label: string) => void;
  copied: string | null;
}) {
  const FLAG = 'brave://flags/#file-system-access-api';

  return (
    <>
      <p className="label">Two clicks away</p>
      <h1 className="mt-3 text-title font-medium">Brave switches off folder access by default</h1>
      <p className="mt-3 text-body text-ink-300">
        Brave is built on Chromium, so the capability is already in your browser. It just ships
        switched off for privacy. Turn it on and m3cull works fully, with no need to change browser.
      </p>

      <ol className="mt-7 flex flex-col gap-4">
        <Step n={1}>
          <span>Open this address in a new tab:</span>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="rounded border border-ink-700 bg-ink-850 px-2.5 py-1.5 font-mono text-caption text-ink-100">
              {FLAG}
            </code>
            <button className="btn-ghost" onClick={() => onCopy(FLAG, 'flag')}>
              {copied === 'flag' ? <Check size={14} /> : <Copy size={14} />}
              {copied === 'flag' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="mt-1.5 text-tiny text-ink-500">
            Brave blocks links to its own settings pages, so it has to be pasted in.
          </p>
        </Step>

        <Step n={2}>
          Set <strong className="font-medium text-ink-100">File System Access API</strong> to{' '}
          <strong className="font-medium text-ink-100">Enabled</strong>.
        </Step>

        <Step n={3}>
          Hit <strong className="font-medium text-ink-100">Relaunch</strong>, then come back here
          and reload.
        </Step>
      </ol>

      <div className="mt-7 flex flex-wrap gap-2.5">
        <button className="btn-primary" onClick={() => location.reload()}>
          I&rsquo;ve enabled it, reload
        </button>
      </div>

      <p className="mt-5 text-tiny text-ink-500">
        Prefer not to change it? Chrome, Edge and Arc have this on out of the box.
      </p>
    </>
  );
}

function OnAPhone({
  onCopy,
  copied,
}: {
  onCopy: (text: string, label: string) => void;
  copied: string | null;
}) {
  return (
    <>
      <p className="label">On a phone</p>
      <h1 className="mt-3 text-title font-medium">m3cull needs a computer</h1>
      <p className="mt-3 text-body text-ink-300">
        No mobile browser can hand a web app a folder yet: not Safari, and not Chrome for Android
        either. Without that, m3cull cannot read your card or write your keepers back out.
      </p>
      <p className="mt-3 text-body text-ink-400">
        Chrome is working on it for a future Android release. Until then, culling happens on a
        laptop or desktop.
      </p>

      <div className="mt-7 flex flex-wrap gap-2.5">
        <button className="btn-primary" onClick={() => onCopy(location.href, 'link')}>
          {copied === 'link' ? <Check size={15} /> : <Copy size={15} />}
          {copied === 'link' ? 'Link copied' : 'Copy link for your computer'}
        </button>
      </div>

      <div className="mt-6 flex items-start gap-2.5 text-tiny text-ink-500">
        <Monitor size={14} className="mt-0.5 shrink-0" />
        <span>
          On the desktop it works in Chrome, Edge and Arc. Brave works too, once its File System
          Access API flag is switched on.
        </span>
      </div>
    </>
  );
}

function NoApi({
  onCopy,
  copied,
}: {
  onCopy: (text: string, label: string) => void;
  copied: string | null;
}) {
  return (
    <>
      <p className="label">Browser support</p>
      <h1 className="mt-3 text-title font-medium">m3cull needs Chrome, Edge or Arc</h1>
      <p className="mt-3 text-body text-ink-300">
        Reading your photos works in every browser. Writing the ones you keep back into a folder
        you choose only works in Chromium browsers. Safari and Firefox have no way to save files
        into a folder you pick.
      </p>
      <p className="mt-3 text-body text-ink-400">
        Rather than let you cull for half an hour and then strand your selects, m3cull says so now.
        Support for these browsers is planned, using downloads instead of folder writes.
      </p>

      <div className="mt-7 flex flex-wrap gap-2.5">
        <button className="btn-primary" onClick={() => onCopy(location.href, 'link')}>
          {copied === 'link' ? <Check size={15} /> : <Copy size={15} />}
          {copied === 'link' ? 'Link copied' : 'Copy link for Chrome'}
        </button>
      </div>

      <p className="mt-4 text-tiny text-ink-500">
        Using Brave? It works too, but the setting has to be switched on first:{' '}
        <span className="font-mono">brave://flags/#file-system-access-api</span>.
      </p>
    </>
  );
}

function TooOld() {
  return (
    <>
      <p className="label">Browser support</p>
      <h1 className="mt-3 text-title font-medium">This browser is too old for m3cull</h1>
      <p className="mt-3 text-body text-ink-300">
        m3cull decodes your photos on the graphics card so navigation stays instant. That needs{' '}
        <span className="font-mono text-ink-200">createImageBitmap</span> and{' '}
        <span className="font-mono text-ink-200">OffscreenCanvas</span>, which this browser does
        not have. An update should be all it takes.
      </p>
      <a
        className="btn-outline mt-7 inline-flex"
        href="https://www.google.com/chrome/"
        target="_blank"
        rel="noreferrer"
      >
        <ExternalLink size={15} />
        Get a current browser
      </a>
    </>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3.5">
      <span className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-ink-700 font-mono text-tiny text-ink-300">
        {n}
      </span>
      <div className="text-body text-ink-300">{children}</div>
    </li>
  );
}
